// PlannerFamilyAccordion.js - Matching ProductSelector with category filtering and hover effect
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { createPortal } from "react-dom"
import gsap from "gsap"
import { fetchProductStructure } from "./api"
import placeholder from "./assets/placeholder.jpg"
import { FaSpinner } from "react-icons/fa"
import { getTranslations } from "./components/translations"

const ITEMS_PER_BATCH = 15
const FETCH_DEBOUNCE_TIME = 500

// Hover image config
const IMAGE_SIZE = 200
const IMAGE_HEIGHT = IMAGE_SIZE
const IMAGE_GAP = 15
const IMAGE_POSITION = "right"
const FOLLOW_SPEED = 0.2
const MAX_ROTATION = 1

function PlannerFamilyAccordion({
  products,
  selectedCategory, // Changed from selectedFamily
  onProductAdd,
  onProductDrag,
  language = "it",
  emptyImageRef,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [previewImages, setPreviewImages] = useState({})
  const [loadingStates, setLoadingStates] = useState({})
  const [failedImages, setFailedImages] = useState({})
  const [visibleProducts, setVisibleProducts] = useState([])
  const [contentHeight, setContentHeight] = useState(0)
  const [productStructure, setProductStructure] = useState({})

  // Hover image state
  const [currentHoverIndex, setCurrentHoverIndex] = useState(-1)

  const translations = getTranslations(language)

  // Refs
  const dropdownRef = useRef(null)
  const contentRef = useRef(null)
  const scrollRef = useRef(null)
  const fetchQueueRef = useRef(new Set())
  const fetchTimeoutRef = useRef(null)
  const toggleClickRef = useRef(false)
  const prevCategoryRef = useRef(selectedCategory)

  // GSAP hover animation refs
  const floatingContainerRef = useRef(null)
  const posRef = useRef({ x: 0, y: 0 })
  const prevPosRef = useRef({ x: 0, y: 0 })
  const velocityRef = useRef({ x: 0 })
  const rotationRef = useRef(0)
  const targetRotationRef = useRef(0)
  const rafIdRef = useRef(null)
  const lastTimeRef = useRef(0)
  const isHoveringRef = useRef(false)
  const xToRef = useRef(null)
  const yToRef = useRef(null)

  // Load product structure on mount
  useEffect(() => {
    const loadStructure = async () => {
      try {
        const structure = await fetchProductStructure()
        setProductStructure(structure)
      } catch (error) {
        console.error("Failed to load product structure:", error)
      }
    }
    loadStructure()
  }, [])

  // Initialize GSAP hover animation
  useEffect(() => {
    if ("ontouchstart" in window || navigator.maxTouchPoints > 0) return

    const initGsap = () => {
      if (!floatingContainerRef.current) return

      gsap.set(floatingContainerRef.current, {
        opacity: 0,
        scale: 0,
        force3D: true,
      })

      xToRef.current = gsap.quickTo(floatingContainerRef.current, "x", {
        duration: FOLLOW_SPEED,
        ease: "power3.out",
      })
      yToRef.current = gsap.quickTo(floatingContainerRef.current, "y", {
        duration: FOLLOW_SPEED,
        ease: "power3.out",
      })

      const track = (timestamp) => {
        if (!lastTimeRef.current) lastTimeRef.current = timestamp
        const deltaTime = timestamp - lastTimeRef.current
        lastTimeRef.current = timestamp

        const dx = posRef.current.x - prevPosRef.current.x

        if (deltaTime > 0) {
          velocityRef.current.x = velocityRef.current.x * 0.7 + dx * 0.3
        }

        prevPosRef.current.x = posRef.current.x
        prevPosRef.current.y = posRef.current.y

        const velocityInfluence = velocityRef.current.x * 0.5
        targetRotationRef.current = Math.max(
          -MAX_ROTATION,
          Math.min(MAX_ROTATION, velocityInfluence),
        )

        if (isHoveringRef.current) {
          rotationRef.current +=
            (targetRotationRef.current - rotationRef.current) * 0.2
        } else {
          rotationRef.current *= 0.92
        }

        if (floatingContainerRef.current) {
          gsap.set(floatingContainerRef.current, {
            rotation: rotationRef.current,
          })
        }

        rafIdRef.current = requestAnimationFrame(track)
      }

      rafIdRef.current = requestAnimationFrame(track)
    }

    const timer = setTimeout(initGsap, 100)

    return () => {
      clearTimeout(timer)
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
      }
      if (floatingContainerRef.current) {
        gsap.killTweensOf(floatingContainerRef.current)
      }
    }
  }, [])

  const ANIMATION_DURATION = 0.5

  const getImagePosition = useCallback((clientX, clientY) => {
    switch (IMAGE_POSITION) {
      case "top":
        return { x: clientX - IMAGE_SIZE / 2, y: clientY - IMAGE_HEIGHT - IMAGE_GAP }
      case "left":
        return { x: clientX - IMAGE_SIZE - IMAGE_GAP, y: clientY - IMAGE_HEIGHT / 2 }
      case "right":
        return { x: clientX + IMAGE_GAP, y: clientY - IMAGE_HEIGHT / 2 }
      case "bottom":
      default:
        return { x: clientX - IMAGE_SIZE / 2, y: clientY + IMAGE_GAP }
    }
  }, [])

  const showFloatingImage = useCallback(
    (index, e) => {
      if ("ontouchstart" in window || navigator.maxTouchPoints > 0) return
      if (!floatingContainerRef.current) return

      const wasHovering = isHoveringRef.current

      setCurrentHoverIndex(index)
      isHoveringRef.current = true

      const pos = getImagePosition(e.clientX, e.clientY)
      posRef.current.x = pos.x
      posRef.current.y = pos.y

      if (!wasHovering) {
        gsap.set(floatingContainerRef.current, {
          x: posRef.current.x,
          y: posRef.current.y,
        })

        gsap.fromTo(
          floatingContainerRef.current,
          { opacity: 0, scale: 0.5 },
          {
            opacity: 1,
            scale: 1,
            duration: ANIMATION_DURATION * 0.6,
            ease: "back.out(1.7)",
            force3D: true,
          },
        )
      }

      const allImages = floatingContainerRef.current.querySelectorAll("[data-index]")
      allImages.forEach((img) => {
        if (img && parseInt(img.getAttribute("data-index")) !== index) {
          gsap.killTweensOf(img)
          gsap.set(img, { opacity: 0, scale: 0.5 })
        }
      })

      const currentImageEl = floatingContainerRef.current.querySelector(
        `[data-index="${index}"]`,
      )
      if (currentImageEl) {
        gsap.killTweensOf(currentImageEl)
        gsap.fromTo(
          currentImageEl,
          { opacity: 0, scale: 0.5 },
          {
            opacity: 1,
            scale: 1,
            duration: ANIMATION_DURATION * 0.5,
            ease: "back.out(1.4)",
            force3D: true,
          },
        )
      }
    },
    [getImagePosition],
  )

  const hideFloatingImage = useCallback(() => {
    isHoveringRef.current = false
    setCurrentHoverIndex(-1)

    if (floatingContainerRef.current) {
      gsap.to(floatingContainerRef.current, {
        opacity: 0,
        scale: 0,
        duration: ANIMATION_DURATION * 0.6,
        ease: "expo.out",
        force3D: true,
      })

      const allImages = floatingContainerRef.current.querySelectorAll("[data-index]")
      allImages.forEach((img) => {
        if (img) {
          gsap.to(img, {
            opacity: 0,
            scale: 0,
            duration: ANIMATION_DURATION * 0.4,
            ease: "expo.out",
            force3D: true,
          })
        }
      })
    }
  }, [])

  const moveFloatingImage = useCallback(
    (e) => {
      if (!isHoveringRef.current) return

      const pos = getImagePosition(e.clientX, e.clientY)
      posRef.current.x = pos.x
      posRef.current.y = pos.y

      xToRef.current?.(posRef.current.x)
      yToRef.current?.(posRef.current.y)
    },
    [getImagePosition],
  )

  // Products filtered by selected category (same logic as ProductSelector)
  // Also filter out products without projectCode - they can't be added to planner
  const categoryProducts = useMemo(() => {
    // First filter: must have projectCode to be usable in planner
    const validProducts = (products || []).filter((product) => {
      return product.projectCode && product.projectCode.trim() !== ""
    })

    if (!selectedCategory || !productStructure[selectedCategory]) return validProducts

    const familiesInCategory = productStructure[selectedCategory] || []

    return validProducts.filter((product) => {
      const productFamily = product._family || product.family || ""
      return familiesInCategory.includes(productFamily)
    })
  }, [products, selectedCategory, productStructure])

  // When category changes, reset visible products and open accordion
  useEffect(() => {
    if (selectedCategory && selectedCategory !== prevCategoryRef.current) {
      setVisibleProducts([])
      fetchQueueRef.current.clear()
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current)
      }
      setIsOpen(true)
      prevCategoryRef.current = selectedCategory
    }
  }, [selectedCategory])

  // Update content height
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight)
    } else {
      setContentHeight(0)
    }
  }, [isOpen, visibleProducts])

  // Load initial products when accordion opens
  useEffect(() => {
    if (isOpen && categoryProducts.length > 0 && visibleProducts.length === 0) {
      const initialBatch = categoryProducts.slice(0, ITEMS_PER_BATCH)
      setVisibleProducts(initialBatch)
      initialBatch.forEach((product) => {
        if (!previewImages[product.code] && !failedImages[product.code]) {
          queueImageFetch(product)
        }
      })
    }
  }, [isOpen, categoryProducts])

  const fetchProductImage = useCallback(
    async (product) => {
      if (
        !product?.code ||
        loadingStates[product.code] ||
        previewImages[product.code] ||
        failedImages[product.code]
      ) {
        return
      }

      setLoadingStates((prev) => ({ ...prev, [product.code]: true }))

      try {
        const productUrl = `https://emersya.com/jsShowcase/${product.code}?container=emersyaIframe`
        const response = await fetch(productUrl)
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
        const data = await response.text()
        const viewerPreviewImageMatch = data.match(
          /src="(https:\/\/cdn\.emersya\.com\/cl\/[^"]+\/preview\.png\?date=[^"]+)"/,
        )

        if (viewerPreviewImageMatch) {
          setPreviewImages((prev) => ({
            ...prev,
            [product.code]: viewerPreviewImageMatch[1],
          }))
        } else {
          setFailedImages((prev) => ({ ...prev, [product.code]: true }))
        }
      } catch (error) {
        console.warn(`Failed to fetch preview for ${product.code}:`, error.message)
        setFailedImages((prev) => ({ ...prev, [product.code]: true }))
      } finally {
        setLoadingStates((prev) => {
          const newState = { ...prev }
          delete newState[product.code]
          return newState
        })
      }
    },
    [loadingStates, previewImages, failedImages],
  )

  const processFetchQueue = useCallback(() => {
    const queue = Array.from(fetchQueueRef.current)
    fetchQueueRef.current.clear()
    queue.forEach((product) => fetchProductImage(product))
  }, [fetchProductImage])

  const queueImageFetch = useCallback(
    (product) => {
      if (!product?.code) return
      fetchQueueRef.current.add(product)
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current)
      }
      fetchTimeoutRef.current = setTimeout(() => processFetchQueue(), FETCH_DEBOUNCE_TIME)
    },
    [processFetchQueue],
  )

  // Handle drag start - FIXED: don't prevent default, let native drag work
  const handleDragStart = (e, product) => {
    if (onProductDrag) {
      const plannerProduct = {
        SKU: product.onpagecode || product.name,
        projectCode: product.projectCode,
        code: product.code,
        name: product.name,
        previewPath: previewImages[product.code] || placeholder,
      }

      // Call parent handler — it sends startProductNodeDrop to Emersya
      // and sets the drag image. Don't set dataTransfer here (vanilla example
      // comments it out) as it can interfere with Emersya's drag handling.
      onProductDrag(plannerProduct, e)
    }
  }

  const handleProductClick = (e, product) => {
    e.stopPropagation()
    hideFloatingImage()
    if (onProductAdd) {
      onProductAdd(product.code, previewImages[product.code] || null)
    }
  }

  const toggleAccordion = (e) => {
    e.stopPropagation()
    toggleClickRef.current = true
    setIsOpen((prev) => !prev)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !toggleClickRef.current &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
      toggleClickRef.current = false
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const getProductName = (product) => {
    if (product.lang && product.lang[language]) {
      return product.lang[language]
    }
    return product.name || product.code
  }

  return (
    <div ref={dropdownRef} className="ay-border-b ay-border-gray-300">
      {/* Header */}
      <button
        onClick={toggleAccordion}
        className="ay-w-full ay-flex ay-items-end ay-py-4 ay-px-0 ay-transition-colors"
      >
        <div className="ay-flex ay-w-1/3 md:ay-w-1/5 ay-items-end ay-pb-0.5">
          <span className="ay-text-[12px] ay-font-serif ay-font-bold ay-text-gray-600 ay-uppercase ay-tracking-wider ay-text-left">
            MODELLO
          </span>
        </div>
        <div className="ay-flex ay-w-2/3 md:ay-w-4/5 ay-items-end ay-justify-between">
          <span className="ay-text-base md:ay-text-xl ay-leading-5 ay-font-sans ay-font-normal ay-text-gray-400">
            {translations.selectModel || "Seleziona modello"}
          </span>
          <svg
            className={`ay-w-4 ay-h-4 ay-text-gray-400 ay-transition-transform ay-duration-300 ${
              isOpen ? "ay-rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Content */}
      <div
        className="ay-overflow-hidden ay-transition-[max-height] ay-duration-300 ay-ease-in-out"
        style={{ maxHeight: `${contentHeight}px` }}
      >
        <div className="ay-flex">
          <div className="ay-w-1/3 md:ay-w-1/5"></div>
          <div className="ay-w-2/3 md:ay-w-4/5">
            <div
              ref={contentRef}
              className="ay-bg-white ay-max-h-[60vh] ay-overflow-y-auto"
            >
              {categoryProducts.length === 0 ? (
                <div className="ay-py-4 ay-text-center ay-text-sm ay-text-gray-500">
                  {selectedCategory
                    ? "Nessun modello disponibile"
                    : "Seleziona prima una categoria"}
                </div>
              ) : (
                <div
                  ref={scrollRef}
                  className="ay-flex ay-flex-col"
                  onMouseMove={moveFloatingImage}
                  onMouseLeave={hideFloatingImage}
                >
                  {visibleProducts.map((product, index) => (
                    <div
                      key={product.code}
                      draggable
                      onDragStart={(e) => handleDragStart(e, product)}
                      onClick={(e) => handleProductClick(e, product)}
                      onMouseEnter={(e) => showFloatingImage(index, e)}
                      className="ay-group/model ay-flex ay-items-center ay-gap-3 ay-py-1.5 md:ay-py-3 ay-pl-2 ay-text-left ay-border-b ay-border-gray-200 last:ay-border-b-0 ay-cursor-grab active:ay-cursor-grabbing"
                      title={`${getProductName(product)} - Trascina o clicca per aggiungere`}
                    >
                      {/* Thumbnail */}
                      <div className="ay-w-10 ay-h-10 ay-flex-shrink-0 ay-bg-gray-100 ay-flex ay-items-center ay-justify-center ay-overflow-hidden">
                        {loadingStates[product.code] ? (
                          <FaSpinner className="ay-animate-spin ay-text-sm ay-text-gray-400" />
                        ) : previewImages[product.code] ? (
                          <img
                            src={previewImages[product.code]}
                            alt={getProductName(product)}
                            className="ay-w-full ay-h-full ay-object-cover"
                          />
                        ) : (
                          <img
                            src={placeholder}
                            alt={getProductName(product)}
                            className="ay-w-full ay-h-full ay-object-cover ay-opacity-40"
                          />
                        )}
                      </div>

                      {/* Name */}
                      <span className="ay-font-sans ay-font-bold ay-text-base md:ay-text-[27px] ay-flex-1 ay-transition-[color,transform] ay-duration-200 ay-ease-out ay-origin-left ay-text-gray-700 group-hover/model:ay-text-gray-500 group-hover/model:ay-skew-x-[-4deg]">
                        {getProductName(product)}
                      </span>
                    </div>
                  ))}

                  {/* Load more */}
                  {visibleProducts.length < categoryProducts.length && (
                    <button
                      onClick={() => {
                        const currentLength = visibleProducts.length
                        const nextBatch = categoryProducts.slice(
                          currentLength,
                          currentLength + ITEMS_PER_BATCH,
                        )
                        setVisibleProducts((prev) => [...prev, ...nextBatch])
                        nextBatch.forEach((product) => {
                          if (!previewImages[product.code] && !failedImages[product.code]) {
                            queueImageFetch(product)
                          }
                        })
                      }}
                      className="ay-py-3 ay-text-sm ay-text-gray-500 hover:ay-text-gray-700 ay-transition-colors ay-text-center"
                    >
                      Carica altri...
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Floating hover image - GSAP controlled */}
      {createPortal(
        <div
          ref={floatingContainerRef}
          className="ay-hidden md:ay-block"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: IMAGE_SIZE,
            height: IMAGE_HEIGHT,
            pointerEvents: "none",
            zIndex: 99999,
            borderRadius: "6px",
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            backgroundColor: "#f5f5f5",
            willChange: "transform, opacity",
            opacity: 0,
            transform: "scale(0)",
          }}
        >
          {visibleProducts.map((product, index) => (
            <img
              key={product.code}
              data-index={index}
              src={previewImages[product.code] || placeholder}
              alt=""
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                opacity: 0,
                willChange: "transform, opacity",
                transform: "scale(0)",
              }}
            />
          ))}
        </div>,
        document.body,
      )}
    </div>
  )
}

export default PlannerFamilyAccordion
